import { faPlus, faSyringe, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Col, Input, message, Modal, Row, Card, Typography, DatePicker as AntDatePicker } from 'antd'
import dayjs from 'dayjs'
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { handleError } from '../../../../lib/utils'
import DesktopMedicalHistoryNav from './Nav'

import { updateSurgeries } from '../../api/api'
import patMedHistReducer from '../../utils/reducer'
import '../style/DesktopSurgeries.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const { Text } = Typography

const DesktopSurgeries = ({ setFacet, setSaving }) => {
  const patMedHist = useSelector((state) => state.patMedHist.data) || {}
  const [surgeryList, setSurgeryList] = useState(patMedHist.surgicalHistory?.list || [])
  const [inputValue, setInputValue] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)

  const dispatch = useDispatch()

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const initialState = useRef(null)

  // Capture initial state when component loads - with delay to ensure state is loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialState.current && surgeryList) {
        initialState.current = {
          surgeryList: JSON.parse(JSON.stringify(surgeryList))
        }
        console.log('Initial surgeries state captured:', initialState.current)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [surgeryList])

  // Clear initial state when component unmounts
  useEffect(() => {
    return () => {
      initialState.current = null
    }
  }, [])

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialState.current) return false

    const hasChanges = JSON.stringify(surgeryList) !== JSON.stringify(initialState.current.surgeryList)
    console.log('Surgeries change detection:', {
      hasChanges,
      initial: initialState.current.surgeryList,
      current: surgeryList
    })

    return hasChanges
  }

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFunction) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFunction)
      setShowUnsavedModal(true)
    } else {
      navigationFunction()
    }
  }

  // Update initial state after successful save
  const updateInitialStateAfterSave = () => {
    initialState.current = {
      surgeryList: JSON.parse(JSON.stringify(surgeryList))
    }
    console.log('Initial state updated after save:', initialState.current)
  }

  // Wrapper for handleSubmit that also updates initial state
  const handleSaveWithStateUpdate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle saving and then navigating
  const handleSaveAndNavigate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
      setShowUnsavedModal(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle navigation without saving
  const handleNavigateWithoutSaving = () => {
    setShowUnsavedModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  // Helper function to convert text to title case
  const toTitleCase = (str) => {
    if (!str) return str
    return str.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const handleSubmit = async () => {
    // Check if we have a valid medical history record
    if (!patMedHist._id) {
      message.error('Medical history record not found. Please refresh the page and try again.')
      return
    }

    setSaving(true)
    try {
      const newRecord = await updateSurgeries(patMedHist._id, surgeryList)
      dispatch(patMedHistReducer.actions.updateState(newRecord))
      setFacet(null)
    } catch (error) {
      message.error(handleError(error))
    } finally {
      setSaving(false)
    }
  }

  const handleAddSurgery = () => {
    if (!inputValue.trim()) {
      message.error('Please provide a surgery description')
      return
    }

    setSurgeryList([
      ...surgeryList,
      {
        description: inputValue.trim(),
        date: selectedDate
      }
    ])
    setInputValue('')
    setSelectedDate(null)
    setShowModal(false)
  }

  const handleRemoveSurgery = (index) => {
    setSurgeryList(surgeryList.filter((_, i) => i !== index))
  }

  return (
    <div className='desktop-surgeries-container'>
      <DesktopSectionHeader
        icon={faSyringe}
        title='Surgical History'
        subtitle='Track any surgeries or medical procedures you have had'
        gradient='linear-gradient(135deg, #D32F2F 0%, #EF5350 100%)'
      />

      <div className='desktop-surgeries-content'>
        {/* Surgeries List with Add Button */}
        <Row gutter={[24, 24]}>
          {surgeryList.map((surgery, index) => (
            <Col xs={24} sm={12} lg={12} xl={8} key={index}>
              <Card className='desktop-surgery-card'>
                <div className='desktop-surgery-header'>
                  <div className='desktop-surgery-info'>
                    <div className='desktop-surgery-name-row'>
                      <FontAwesomeIcon icon={faSyringe} className='desktop-surgery-icon' />
                      <h4 className='desktop-surgery-name'>{toTitleCase(surgery.description)}</h4>
                    </div>
                    <div className='desktop-surgery-dates'>
                      <Typography.Text type='secondary'>
                        <strong>Surgery Date:</strong>{' '}
                        {surgery.date ? dayjs(surgery.date).format('DD/MM/YYYY') : 'Unknown date'}
                      </Typography.Text>
                    </div>
                  </div>
                  <Button
                    danger
                    type='text'
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => handleRemoveSurgery(index)}
                    className='desktop-remove-surgery-btn'
                  />
                </div>
              </Card>
            </Col>
          ))}

          {/* Add New Surgery Button - appears to the right of the last surgery */}
          {surgeryList.length > 0 && (
            <Col xs={24} sm={12} lg={12} xl={8}>
              <Card className='desktop-add-surgery-card'>
                <Button
                  type='primary'
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() => setShowModal(true)}
                  size='large'
                  className='desktop-add-surgery-btn'
                >
                  Add Surgery
                </Button>
              </Card>
            </Col>
          )}
        </Row>

        {/* Empty State Message with Add Button */}
        {surgeryList.length === 0 && (
          <>
            <Card className='desktop-empty-state'>
              <div className='desktop-empty-content'>
                <FontAwesomeIcon icon={faSyringe} className='desktop-empty-icon' />
                <h3>No Surgeries Added</h3>
                <p>Click "Add Surgery" below to start tracking your surgical history.</p>
              </div>
            </Card>

            {/* Add New Surgery Button - appears below empty state */}
            <Card className='desktop-add-surgery-card'>
              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setShowModal(true)}
                size='large'
                className='desktop-add-surgery-btn'
              >
                Add Surgery
              </Button>
            </Card>
          </>
        )}
      </div>

      {/* Add Surgery Modal */}
      <Modal
        title='Add Surgery'
        open={showModal}
        onCancel={() => {
          setShowModal(false)
          setInputValue('')
          setSelectedDate(null)
        }}
        footer={null}
        className='desktop-surgery-modal'
      >
        <div className='desktop-modal-content'>
          <div className='desktop-form-field'>
            <label>Surgery Description</label>
            <Input.TextArea
              placeholder='Enter surgery or procedure details...'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              rows={3}
              style={{ resize: 'none' }}
            />
          </div>

          <div className='desktop-form-field'>
            <label>Surgery Date</label>
            <AntDatePicker
              placeholder='Select surgery date'
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={(date) => {
                setSelectedDate(date ? date.format('YYYY-MM-DD') : null)
              }}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              style={{ width: '100%', height: '40px' }}
              format='YYYY-MM-DD'
            />
          </div>

          <div className='desktop-modal-actions'>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type='primary' onClick={handleAddSurgery}>
              Add Surgery
            </Button>
          </div>
        </div>
      </Modal>

      {/* Unsaved Changes Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faSyringe} style={{ color: '#ff6b6b' }} />
            <span>Unsaved Changes</span>
          </div>
        }
        open={showUnsavedModal}
        onCancel={() => setShowUnsavedModal(false)}
        footer={null}
        className='desktop-unsaved-changes-modal'
        centered
      >
        <div className='desktop-unsaved-changes-content'>
          <Typography.Text className='desktop-unsaved-changes-message'>
            You have unsaved changes to your surgical history information. What would you like to do?
          </Typography.Text>

          <div className='desktop-unsaved-changes-actions'>
            <Button type='primary' className='desktop-save-changes-btn' onClick={handleSaveAndNavigate} loading={false}>
              Save Changes
            </Button>
            <Button type='default' className='desktop-discard-changes-btn' onClick={handleNavigateWithoutSaving}>
              Discard Changes
            </Button>
            <Button type='text' className='desktop-cancel-modal-btn' onClick={() => setShowUnsavedModal(false)}>
              Continue Editing
            </Button>
          </div>
        </div>
      </Modal>

      <DesktopMedicalHistoryNav
        prevFunction={() => handleNavigation(() => setFacet(null))}
        nextFunction={handleSaveWithStateUpdate}
        prevText='Back to Overview'
        nextText='Save Surgeries'
      />
    </div>
  )
}

export default DesktopSurgeries
